import { useQuery } from "@apollo/client";
import { useAppDispatch } from "hooks/utilsHooks/storeHooks";
import { useEffect } from "react";
import { batch } from "react-redux";
import { setQrCode } from "slices/accessSlice";
import { setInitialDishTypes } from "slices/dishesSlice";
import { setDoorLockState } from "slices/doorSlice";
import { setFridgeErrorType, setFridgeId, setFridgeState, setSegments } from "slices/fridgeInfoSlice";
import { setLightingMode } from "slices/fridgeLightingSlice";
import { clearTransactionSlot, setTransaction } from "slices/transactionSlice";
import type { AppDispatch } from "store";
import { ErrorType, type Fridge } from "types";
import type { LocalFridgeState } from "types/mainTypes";
import { setFridgeNameToLocalStorage, setupNewFridgeId } from "utils/browser/localStorage";
import { INITIAL_STATE_QUERY } from "utils/gqlOperations";
import { logToSentry } from "utils/logger";
import { deepOmit } from "utils/utils";
import { setInitialDataLoaded } from "../../slices/appStateSlice";

export const handleSetInitialData = (data: Fridge | LocalFridgeState, appDispatch: AppDispatch) => {
	const cleanedData = deepOmit(data, ["__typename"]) as LocalFridgeState;
	setFridgeNameToLocalStorage(cleanedData.name);
	batch(() => {
		appDispatch(setFridgeId(cleanedData.id));
		appDispatch(setFridgeState(cleanedData.state ?? null));
		appDispatch(setFridgeErrorType(cleanedData.error?.type ?? null));
		if (cleanedData.access?.qrCode?.code) {
			appDispatch(setQrCode(cleanedData.access.qrCode.code));
		}
		if (cleanedData.dishes?.types) {
			appDispatch(setInitialDishTypes(cleanedData.dishes?.types));
		}
		if (cleanedData.segments) {
			appDispatch(setSegments(cleanedData.segments));
		}
		if (cleanedData.door) {
			appDispatch(setDoorLockState(cleanedData.door));
		}
		if (cleanedData.lighting?.mode != null) {
			appDispatch(setLightingMode(cleanedData.lighting.mode));
		}
		if (cleanedData.transaction?.orders?.last || cleanedData.transaction?.refills?.last) {
			if (cleanedData.transaction.refills?.last) {
				logToSentry(
					`Initial state has active refill on fridge: ${cleanedData.id}, mode: ${cleanedData.transaction.refills.last.mode}`,
					"info",
				);
			}
			// "snapshot": this also runs on reconnect recovery while the pre-outage slot is still held, so a
			// state transition it reveals happened at an unknown time — the scan timing must not stamp it
			// (JID-2348). The slot itself is still landed so the UI recovers.
			appDispatch(
				setTransaction(
					{
						order: cleanedData.transaction.orders?.last ?? null,
						refill: cleanedData.transaction.refills?.last ?? null,
					},
					"snapshot",
				),
			);
		}
		if (!cleanedData.transaction) {
			// The snapshot says nothing is running: clear whatever transaction we held (any kind),
			// so a stale flow (e.g. a remote-refill modal) cannot survive a reconnect.
			appDispatch(clearTransactionSlot());
		}
	});
	appDispatch(setInitialDataLoaded(true));
};

type Props = {
	fridgeId: string;
};

export const useInitialStateQuery = ({ fridgeId }: Props) => {
	const appDispatch = useAppDispatch();

	const { data, loading, refetch } = useQuery(INITIAL_STATE_QUERY, {
		variables: { id: fridgeId },
		fetchPolicy: "network-only",
		refetchWritePolicy: "overwrite",
		onCompleted: ({ fridge }) => {
			// A { fridge: null } completion (abnormal backend answer; also the shape of a failing
			// recovery refetch) carries no usable state — running the fan-out on it would smear
			// undefined id/name into Redux and clear the error flag though nothing recovered.
			if (fridge) {
				handleSetInitialData(fridge, appDispatch);
			}
		},
	});

	useEffect(() => {
		if (data?.fridge?.error && data.fridge.error.type === ErrorType.UNAUTHORIZED) {
			// Prompt user to provide a valid UUID
			setupNewFridgeId(fridgeId, "Unauthorized access. Please provide a valid UUID");
		}
	}, [data?.fridge?.error, fridgeId]);

	return {
		loading: loading,
		refetch: refetch,
		data: data,
	};
};
